import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useNavigation, useFocusEffect } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { SringarService } from "../../../services/SringarService";

type TotalSringarsDto = {
    sringarId: string | null;
    isGuruPuja: boolean;
    totalMarks: number;
    marksOfSringars: number;
    isEditable: boolean;
};

export function SringarScreen() {
    const navigation = useNavigation<any>();
    const { user } = useAuth();

    const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
    const [date, setDate] = useState(todayStr);

    const [loading, setLoading] = useState(false);

    const [sringarId, setSringarId] = useState<string | null>(null);
    const [isGuruPuja, setIsGuruPuja] = useState<boolean | null>(null);
    const [isEditable, setIsEditable] = useState(true);

    const [marks, setMarks] = useState(0);
    const [totalMarks, setTotalMarks] = useState(0);

    const calcMarks = (v: boolean | null) => (v ? 3 : 0);

    useEffect(() => {
        setMarks(calcMarks(isGuruPuja));
    }, [isGuruPuja]);

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    setDate(format(selectedDate, "yyyy-MM-dd"));
                }
            },
        });
    };

    const loadAgainstDate = useCallback(
        async (dateStr: string) => {
            if (!user?.userId) return;

            try {
                setLoading(true);

                const res = await SringarService.getAgainstDate(dateStr);

                if (res?.status !== 200) {
                    Flash.error(res?.message || "Failed to load");
                    return;
                }

                const data = res.data as TotalSringarsDto | null;

                setSringarId(data?.sringarId ?? null);
                setIsGuruPuja(data?.sringarId ? !!data?.isGuruPuja : null);
                setIsEditable(data?.isEditable ?? true);


                setTotalMarks(Number(data?.totalMarks ?? 0));
                setMarks(Number(data?.marksOfSringars ?? 0));
            } catch (e: any) {
                Flash.error(e?.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        },
        [user?.userId]
    );

    useEffect(() => {
        loadAgainstDate(date);
    }, [date, loadAgainstDate]);

    useFocusEffect(
        useCallback(() => {
            loadAgainstDate(date);
        }, [date, loadAgainstDate])
    );

    const onSave = async () => {
        if (!user?.userId) return Flash.error("User not found");
        if (isGuruPuja === null) return Flash.error("Please select Yes or No");

        try {
            setLoading(true);

            const payload = {
                sringarId: sringarId ?? null,
                userId: user.userId,
                date,
                isGuruPuja,
                marks: calcMarks(isGuruPuja),
            };

            const res = await SringarService.create(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to save");
                return;
            }

            Flash.success("Success!", "Sringar & Guru Pooja Saved Successfully!");
            navigation.navigate("SringarHistory");

            //   await loadAgainstDate(date);
        } catch (e: any) {
            Flash.error(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    const ToggleRow = ({
        label,
        rowMarks,
        checked,
        onToggle,
    }: {
        label: string;
        rowMarks: number;
        checked: boolean;
        onToggle: () => void;
    }) => (
        <Pressable
            onPress={onToggle}
            style={({ pressed }) => [
                styles.toggleRow,
                checked ? styles.toggleRowActive : styles.toggleRowInactive,
                pressed ? { opacity: 0.95 } : null,
            ]}
        >
            <View style={styles.toggleLeft}>
                <View
                    style={[
                        styles.circle,
                        checked ? styles.circleActive : styles.circleInactive,
                    ]}
                >
                    <Text
                        style={[
                            styles.circleIcon,
                            { color: checked ? "#fff" : "rgba(74,35,17,0.35)" },
                        ]}
                    >
                        ✓
                    </Text>
                </View>

                <Text
                    style={[
                        styles.toggleText,
                        { color: checked ? "#4A2311" : "rgba(74,35,17,0.65)" },
                    ]}
                >
                    {label}
                </Text>
            </View>

            <View style={styles.marksPill}>
                <Text style={styles.marksPillText}>{rowMarks} Marks</Text>
            </View>
        </Pressable>
    );

    return (
        <DivineBackground overlayOpacity={0.9}>
            <View style={{ flex: 1 }}>
                <Header title="Sringar & Guru Pooja" showBack />

                <ScrollView contentContainerStyle={styles.container}>
                    {/* SUMMARY */}
                    <View style={styles.summaryCard}>
                        <View style={styles.summaryRow}>
                            <Text style={styles.summaryTitle}>This Month's Offering</Text>

                            <View style={{ alignItems: "flex-end" }}>
                                <Text style={styles.totalLabel}>TOTAL MARKS</Text>
                                <Text style={styles.totalValue}>{totalMarks}</Text>
                            </View>
                        </View>
                    </View>

                    {/* FORM */}
                    <View style={styles.card}>
                        <View style={styles.topRow}>
                            <Pressable onPress={openDatePicker} style={styles.dateChip}>
                                <Text style={styles.dateChipIcon}>📅</Text>
                                <Text style={styles.dateChipText}>
                                    {format(new Date(date), "dd-MM-yyyy")}
                                </Text>
                            </Pressable>

                            <Pressable
                                onPress={() => navigation.navigate("SringarHistory")}
                                style={styles.historyBtn}
                            >
                                <Text style={styles.historyIcon}>🕘</Text>
                                <Text style={styles.historyText}>History</Text>
                            </Pressable>
                        </View>

                        <View style={{ gap: 10, marginTop: 14 }}>
                            <ToggleRow
                                label="Guru Pooja - Yes"
                                rowMarks={3}
                                checked={isGuruPuja === true}
                                onToggle={() => setIsGuruPuja(true)}
                            />

                            <ToggleRow
                                label="Guru Pooja - No"
                                rowMarks={0}
                                checked={isGuruPuja === false}
                                onToggle={() => setIsGuruPuja(false)}
                            />

                            <View style={styles.calcRow}>
                                <Text style={styles.calcLabel}>Calculated Marks:</Text>
                                <Text style={styles.calcValue}>{marks}</Text>
                            </View>
                        </View>

                        {isEditable && (
                            <View style={styles.btnRow}>
                                <Pressable
                                    onPress={() => navigation.goBack()}
                                    style={styles.cancelBtn}
                                    disabled={loading}
                                >
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>

                                <View style={{ flex: 1 }}>
                                    <Button
                                        title={loading ? "Saving..." : "Save"}
                                        onPress={onSave}
                                        disabled={loading}
                                    />
                                </View>
                            </View>
                        )}

                        {loading && (
                            <View style={{ marginTop: 12, alignItems: "center" }}>
                                <ActivityIndicator />
                            </View>
                        )}
                    </View>
                </ScrollView>
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: { padding: 14, paddingBottom: 30, gap: 12 },

    summaryCard: {
        borderRadius: 14,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.75)",
        borderWidth: 1,
        borderColor: "rgba(251,146,60,0.25)",
    },

    summaryRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    summaryTitle: { fontSize: 13, fontWeight: "900", color: "#4A2311" },

    totalLabel: {
        fontSize: 10,
        color: "rgba(74,35,17,0.45)",
        fontWeight: "800",
    },

    totalValue: { fontSize: 26, fontWeight: "900", color: "#F97316" },

    card: {
        borderRadius: 16,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.88)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },

    topRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(255,238,210,0.85)",
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },

    dateChipIcon: { fontSize: 14 },

    dateChipText: { fontSize: 13, fontWeight: "800", color: "#4A2311" },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        height: 34,
        borderRadius: 10,
        borderWidth: 1,
        paddingHorizontal: 10,
        borderColor: "rgba(0,0,0,0.06)",
    },

    historyIcon: { fontSize: 14 },

    historyText: { fontSize: 12, fontWeight: "800", color: "rgba(26,71,49,0.65)" },

    toggleRow: {
        borderRadius: 14,
        padding: 12,
        borderWidth: 1,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    toggleRowInactive: {
        backgroundColor: "rgba(255,255,255,0.55)",
        borderColor: "rgba(0,0,0,0.06)",
    },

    toggleRowActive: {
        backgroundColor: "rgba(255,238,210,0.65)",
        borderColor: "rgba(251,146,60,0.35)",
    },

    toggleLeft: { flexDirection: "row", alignItems: "center", gap: 10 },

    circle: {
        width: 34,
        height: 34,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
    },

    circleInactive: { backgroundColor: "rgba(74,35,17,0.08)" },

    circleActive: { backgroundColor: "#F97316" },

    circleIcon: { fontSize: 16, fontWeight: "900" },

    toggleText: { fontSize: 14, fontWeight: "900" },

    marksPill: {
        paddingHorizontal: 10,
        paddingVertical: 6,
        backgroundColor: "#fff",
        borderRadius: 10,
    },

    marksPillText: { fontSize: 11, fontWeight: "800" },

    calcRow: {
        marginTop: 6,
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        backgroundColor: "rgba(255,238,210,0.55)",
        flexDirection: "row",
        justifyContent: "space-between",
    },

    calcLabel: { fontSize: 13, fontWeight: "800" },

    calcValue: { fontSize: 20, fontWeight: "900", color: "#F97316" },

    btnRow: { flexDirection: "row", gap: 10, marginTop: 14 },

    cancelBtn: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        alignItems: "center",
        justifyContent: "center",
    },

    cancelText: { fontSize: 14, fontWeight: "900" },
});
